import { I18n } from "@assets/strings";
import { List, Range, Set } from "immutable";
import React, { useMemo, useState } from "react";
import styled from "styled-components";
import { useServices } from "@core/servicesContext";
import { DetailedCourse } from "@domain/lms/course/course";
import { Chapter } from "@domain/lms/course/courseContent";
import { Exercise } from "@domain/lms/course/exercise/exercise";
import { LoadingButton } from "@ui/components/buttons/loadingButton";
import { Title } from "@ui/components/containers/title";
import { ExerciseView } from "@ui/components/course/exercise/exerciseView";
import { gap } from "@ui/styles/utils";

interface CourseExercisesProps extends React.HTMLAttributes<HTMLDivElement> {
	course: DetailedCourse;
	chapter: Chapter;
}

export const CourseExercises: React.FC<CourseExercisesProps> = ({ course, chapter, ...htmlProps }) => {
	const [answers, setAnswers] = useState<List<Set<number>>>(List(Range(0, chapter.exercises.size)).map(() => Set()));
	const chapterIndex = useMemo(
		() => course.content.chapters.findIndex((c) => c.id === chapter.id),
		[chapter.id, course.id]
	);

	const [loading, setLoading] = useState(false);
	const { courseService } = useServices();
	const [correctedExercises, setCorrectedExercises] = useState<Exercise[] | null>(null);
	const valid = answers.every((a) => a !== null);
	const usedExercise = correctedExercises ?? chapter.exercises.toArray();
	const isFinished = usedExercise.flatMap((ex) => ex.choices.toArray()).find((c) => !!c.valid);

	return (
		<Container {...htmlProps}>
			<Title>{I18n.t("course.section.qcm.title")}</Title>
			{usedExercise.map((ex, i) => (
				<ExerciseView
					key={ex.id}
					exercise={ex}
					index={i}
					currentAnswer={answers.get(i, Set())}
					onAnswerChosen={(answer) => setAnswers(answers.set(i, answer))}
				/>
			))}
			{!isFinished && (
				<LoadingButton
					disabled={!valid}
					loading={loading}
					onClick={async () => {
						if (!valid) {
							return;
						}
						setLoading(true);
						// FIXME : aucun code ne gère les answers côté backend
						const correction = await courseService.submitExercises(course.id, chapterIndex, List());
						setCorrectedExercises(correction);
						setLoading(false);
					}}
				>
					{I18n.t("validate")}
				</LoadingButton>
			)}
		</Container>
	);
};

const Container = styled.div`
	${gap(20)}
`;
